import { z } from 'zod';

/**
 * A test call that runs SOME of a v2 workflow (ADR-306).
 *
 * The wire shape of `TestPlan` in `@vagusx/agent-core/v2` (`test-plan.ts`),
 * which is where what each field MEANS is documented. Declared here as well
 * because agent-core depends on nothing and the web app and the typed clients
 * need to build and validate one; the two are structurally identical, and the
 * worker hands this straight to agent-core's resolver.
 *
 * SHAPE ONLY. Whether the nodes exist, whether a stub is possible, whether the
 * plan was written for the workflow the call will actually run — all of that
 * needs the definition, which only the worker has, and the worker refuses the
 * call loudly when any of it fails rather than running something else.
 */

/** Same rule as a definition's node ids (`agent-core` `definition.ts`). */
const nodeIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]+$/, 'node id must be alphanumeric with - or _')
  .max(100);

export const nodeOverrideSchema = z.discriminatedUnion('mode', [
  /** Skip the node; it writes nothing. */
  z.object({ node: nodeIdSchema, mode: z.literal('off') }).strict(),
  /**
   * Skip the node and fold what a successful run would have. `choice` picks
   * among several successes — which intent `CallerIntent` records.
   */
  z
    .object({
      node: nodeIdSchema,
      mode: z.literal('stub'),
      choice: z.string().min(1).max(64).optional(),
    })
    .strict(),
]);

export type NodeOverrideInput = z.infer<typeof nodeOverrideSchema>;

export const stepPlanSchema = z
  .object({
    /** The workflow id this plan was written for. Refused on any other. */
    workflow: z.string().min(1).max(100),
    /** Bounded well above any shipped flow's node count. */
    overrides: z.array(nodeOverrideSchema).max(50),
  })
  .strict();

export type StepPlanInput = z.infer<typeof stepPlanSchema>;
